/**
 * Sanitization helper for `candidates.resume_html`.
 *
 * Single source of truth for the DOMPurify config + the post-sanitize
 * hook that hardens external links. The ONLY consumer of this module
 * is `<ResumeDocument>` (carve-out site for `dangerouslySetInnerHTML`).
 *
 * Sec invariant **S-30e-8** (generalized in Batch 30j-2): the global
 * structural test at `frontend/src/__tests__/no-dangerous-html.test.ts`
 * enforces that the only file in `frontend/src/` containing
 * `dangerouslySetInnerHTML` is `ResumeDocument.tsx`, AND that the value
 * passed to `__html` is the result of a `sanitizeResumeHtml(...)` call
 * — raw assignment fails the test.
 *
 * Defense-in-depth layered for `resume_html` per SAAS_ARCHITECTURE.md
 * §"Resume preview rendering":
 *   1. Bounded prompt output contract (RESUME_FORMAT_SYSTEM emits a
 *      tightly-shaped HTML fragment — h1/h2/p/ul/li/a/strong/em).
 *   2. 1 MB length cap at the request schema
 *      (`CandidateCreateRequest.resume_html`,
 *      `CandidateUpdateRequest.resume_html`) and re-enforced as a
 *      defensive pre-parse truncation here.
 *   3. DOMPurify allowlist at render (this file).
 *   4. Global CSP disallowing inline scripts.
 *
 * The `afterSanitizeAttributes` hook is registered at MODULE LOAD (NOT
 * inside `useEffect`) per Sec Lead HARD-10 — `useEffect` runs after
 * first render, which means the first sanitize call would skip the
 * hook. Module-load registration is synchronous and fires before any
 * consumer can call `sanitizeResumeHtml`.
 */
import DOMPurify from "dompurify";

/** Frontend defensive truncation cap (mirrors backend schema cap). */
const RESUME_HTML_MAX_LENGTH = 1_048_576; // 1 MiB

/**
 * Hook: enforce `rel="noopener noreferrer"` on every `<a>` element with
 * `target="_blank"` (and on `<a>` without an explicit target — middle/
 * cmd-click can open a new context with opener inheritance on some
 * user-agents). Token-set merge so any existing `rel` value is
 * preserved.
 *
 * Threat: reverse tabnabbing — a `<a target="_blank">` to an external
 * page can call `window.opener.location = "https://phish/"` and replace
 * the original CertAIn tab with a phishing UI. `noopener` severs
 * `window.opener`; `noreferrer` additionally suppresses the Referer
 * header.
 */
function externalLinkRelHook(node: Element): void {
  if (node.tagName !== "A") return;
  const target = node.getAttribute("target");
  if (target !== "_blank" && target !== null) return;
  const existingRel = node.getAttribute("rel") ?? "";
  const tokens = new Set(existingRel.split(/\s+/).filter(Boolean));
  tokens.add("noopener");
  tokens.add("noreferrer");
  node.setAttribute("rel", Array.from(tokens).join(" "));
}

// Module-load hook registration (Sec Lead HARD-10). The `instanceof
// Element` guard inside the hook is unnecessary because DOMPurify only
// fires `afterSanitizeAttributes` on Element nodes — but we keep the
// signature shape `(node: Element) => void` for type clarity.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  externalLinkRelHook(node);
});

/**
 * Sanitize a stored `resume_html` value for safe rendering via
 * `dangerouslySetInnerHTML`. The output is a plain HTML string.
 *
 * Behavior on oversize input: truncated to 1 MiB BEFORE DOMPurify
 * parses (defense in depth — backend schema also caps writes). A
 * structured `console.warn` is emitted so we can spot the case in
 * production telemetry.
 */
export function sanitizeResumeHtml(rawHtml: string): string {
  let bounded = rawHtml;
  if (rawHtml.length > RESUME_HTML_MAX_LENGTH) {
    console.warn(
      "[sanitizeResumeHtml] input exceeded 1MB cap, truncated",
      { original_length: rawHtml.length },
    );
    bounded = rawHtml.slice(0, RESUME_HTML_MAX_LENGTH);
  }

  return DOMPurify.sanitize(bounded, {
    // Tags emitted by RESUME_FORMAT prompt + `.resume-doc` selectors
    // (foundations §10.5). `b`/`i` are presentational duplicates of
    // `strong`/`em`; dropped per Sec Lead §1.1 — model emits semantic
    // tags by contract, legacy paste-in degrades gracefully.
    ALLOWED_TAGS: [
      "h1",
      "h2",
      "h3",
      "p",
      "ul",
      "ol",
      "li",
      "a",
      "strong",
      "em",
      "br",
      "div",
      "span",
      "section",
      "article",
    ],
    // `class` required for .rd-job / .rd-skills styling. `target` +
    // `rel` so the hook above can enforce noopener/noreferrer.
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    // DOMPurify default URI-safe list covers `class`/`id`/`name`/etc.
    // but NOT `target` / `rel`. Without this addition, DOMPurify
    // attempts to validate the `target="_blank"` value against
    // `ALLOWED_URI_REGEXP` (which expects an `https:` / `mailto:` /
    // `tel:` prefix) and strips the attribute. Marking these two as
    // URI-safe tells DOMPurify the value is opaque to URI semantics —
    // safe because their permitted values are not user-navigable.
    ADD_URI_SAFE_ATTR: ["target", "rel"],
    // Anchored, case-insensitive — `javascript:`, `data:`, `vbscript:`,
    // `file:`, protocol-relative `//evil/x`, etc. all rejected. DOMPurify
    // matches this regex against the FULL URI value (not the scheme
    // alone), so the trailing `.*` is required to accept the path/host
    // portion of `https://example.com/foo`.
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:).*/i,
    // Defense-in-depth: belt + suspenders. Anything not in
    // `ALLOWED_TAGS` is already stripped, but explicit forbid documents
    // intent and survives future allowlist drift.
    FORBID_TAGS: [
      "svg",
      "math",
      "iframe",
      "object",
      "embed",
      "script",
      "style",
    ],
    FORBID_ATTR: [
      "style",
      // Event handlers — explicit list documents intent at the call site.
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onmouseout",
      "onfocus",
      "onblur",
      "onsubmit",
      "onreset",
      "onkeydown",
      "onkeyup",
      "onkeypress",
      "onchange",
      "onselect",
      "ontoggle",
      "onanimationend",
      "onanimationstart",
      "onanimationiteration",
      "ontransitionend",
      "onpointerdown",
      "onpointerup",
      "onpointermove",
      "onauxclick",
      "onbeforeinput",
      "oninput",
      "onwheel",
      "onscroll",
      "oncut",
      "oncopy",
      "onpaste",
      "ondrag",
      "ondragstart",
      "ondragend",
      "ondrop",
      // Non-handler hostile attributes
      "formaction",
      "srcdoc",
      "background",
    ],
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    WHOLE_DOCUMENT: false,
  });
}
